import { ApiRetailerPaymentType } from '@vend/types'
import { useT } from '@transifex/react'
import { useCallback, useEffect } from 'react'
import { useHotkeysService, useRegisterManager } from '../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { selectOutstandingBalance } from '../../../state/slices/currentSale/currentSaleSlice'
import { requestTerminalStatusUpdate } from '../../../state/slices/terminalStatus/terminalStatusSlice'
import { getMinimumLsPayAmount } from '../../../util/lspay-utils'
import { useIsOffline } from '../../shared/useIsOffline'
import { usePaymentRecalculationLock } from './PaymentRecalculationLockContext'

type LSPayEntryPaymentData = {
  lspayFlow: 'cnp'
}

type UseLSPayEntryPaymentActionsParams = {
  paymentType?: ApiRetailerPaymentType
  disabled?: boolean
  pay: (
    payment: ApiRetailerPaymentType,
    paymentData?: LSPayEntryPaymentData
  ) => void
  amountTendered?: number | string
}

export const useLSPayEntryPaymentActions = ({
  paymentType,
  disabled,
  pay,
  amountTendered,
}: UseLSPayEntryPaymentActionsParams) => {
  const dispatch = useAppDispatch()
  const registerManager = useRegisterManager()
  const { isOffline } = useIsOffline()
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const hotkeysService = useHotkeysService()
  const { arePaymentActionsLocked } = usePaymentRecalculationLock()
  const t = useT()

  // Get currency-specific minimum amount from active outlet
  const { minimumAmount, currencySymbol } = getMinimumLsPayAmount(
    registerManager.getActiveOutlet()
  )

  // Use amountTendered if provided, otherwise fall back to outstandingBalance
  const paymentAmount =
    amountTendered !== undefined &&
    amountTendered !== null &&
    amountTendered !== ''
      ? Number(amountTendered)
      : outstandingBalance
  const isAmountBelowMinimum =
    paymentAmount >= 0 && paymentAmount < minimumAmount

  const isDisabled =
    disabled ||
    !paymentType ||
    arePaymentActionsLocked ||
    isOffline ||
    isAmountBelowMinimum

  const handleTerminalPayment = useCallback(() => {
    if (isDisabled || !paymentType) {
      return
    }
    dispatch(requestTerminalStatusUpdate())
    pay(paymentType)
  }, [dispatch, isDisabled, paymentType, pay])

  const handleCardNotPresentPayment = useCallback(() => {
    if (isDisabled || !paymentType) {
      return
    }
    pay(paymentType, { lspayFlow: 'cnp' })
  }, [isDisabled, paymentType, pay])

  useEffect(() => {
    const config = {
      summary: t('Select payment option 1', {
        _context: 'Hot key summary for the Lightspeed Payments pay button',
      }),
      combo: `alt+1`,
      count: true,
      action: handleTerminalPayment,
    }
    hotkeysService.add(config)

    return () => {
      hotkeysService.remove(config.combo)
    }
  }, [t, hotkeysService, handleTerminalPayment])

  return {
    currencySymbol,
    handleCardNotPresentPayment,
    handleTerminalPayment,
    isAmountBelowMinimum,
    isDisabled,
    isOffline,
    minimumAmount,
  }
}
